import React, { Suspense } from "react"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import { useAuthContext } from "contexts/AuthContext"
import {
  UNAUTHENTICATED_ENTRY,
  AUTHENTICATED_ENTRY,
  PROFESSOR_AUTHENTICATED_ENTRY,
} from "configs/AppConfig"
import Loading from "components/Loading"
import { USER_TYPES } from "common/constants"
import { ACTIVITY_VIEW_MODES } from "pages/student/activity/common/constants"
import { TourProvider } from "@reactour/tour"
import PlacementProvider from "contexts/PlacementContext"
import ModuleProvider from "contexts/ModuleContext"
import LicenceProvider from "contexts/LicenceContext"
import { StudentHubProvider } from "contexts/StudentHubContext"
import { useStudentActivityTracker } from "hooks/useStudentActivityTracker"
import { styles as tourStyles } from "pages/student/activity/styles/Activity.Tour.Styles"
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock"
import { isMobile } from "react-device-detect"
import ActiveContentProvider from "contexts/ActiveContentContext"
import { routes } from "common/navigationRoutes"

const StudentActivityTracker = () => {
  useStudentActivityTracker()
  return null
}

const StudentRouter = ({ children }) => {
  const { userData } = useAuthContext()

  const disableBody = (target) => disableBodyScroll(target)
  const enableBody = (target) => enableBodyScroll(target)

  const getViewMode = () => {
    console.log(
      "%c" + process.env.REACT_APP_VERSION,
      "color: red; font-size: 20px;"
    )
    const queryParams = new URLSearchParams(window.location.search)
    const view = parseInt(queryParams.get("view")) || 0
    if (Object.values(ACTIVITY_VIEW_MODES).includes(view)) {
      return view
    }
    return ACTIVITY_VIEW_MODES.STUDENT
  }

  if (userData?.userTypeId !== USER_TYPES.STUDENT) {
    return children
  }

  return (
    <TourProvider
      disableKeyboardNavigation
      showDots={false}
      showCloseButton={false}
      showBadge={false}
      showPrevNextButtons={false}
      styles={tourStyles.tour}
      afterOpen={isMobile ? () => {} : disableBody}
      beforeClose={isMobile ? () => {} : enableBody}
      onClickMask={({ setIsOpen }) => {}}
    >
      <ActiveContentProvider>
        <PlacementProvider>
          <ModuleProvider viewMode={getViewMode()}>
            <LicenceProvider>
              <StudentHubProvider>
                <StudentActivityTracker />
                {children}
              </StudentHubProvider>
            </LicenceProvider>
          </ModuleProvider>
        </PlacementProvider>
      </ActiveContentProvider>
    </TourProvider>
  )
}

const StudentRoutes = () => {
  const { userData, isConfigurationCompleted } = useAuthContext()
  const location = useLocation()

  if (!userData || userData?.userId < 1) {
    return (
      <Navigate
        to={`${UNAUTHENTICATED_ENTRY}?ReturnUrl=${location.pathname}`}
        replace
      />
    )
  }

  // Enforce student access only.
  if (userData.userTypeId !== USER_TYPES.STUDENT) {
    if (userData.userTypeId === USER_TYPES.TEACHER) {
      return <Navigate to={PROFESSOR_AUTHENTICATED_ENTRY} replace />
    }
    return <Navigate to={UNAUTHENTICATED_ENTRY} replace />
  }

  const prefix = process.env.PUBLIC_URL
  const basePath = location.pathname
    .toLowerCase()
    .startsWith(prefix.toLowerCase())
    ? location.pathname.substring(prefix.length)
    : location.pathname

  if (
    !isConfigurationCompleted &&
    !basePath.startsWith(routes.ACCOUNT) &&
    !basePath.startsWith(routes.ONBOARDING)
  ) {
    return <Navigate to="/account?firstSetup=1&welcomeMessage=1" replace />
  }

  if (location.pathname === "/") {
    return (
      <Navigate
        to={
          isConfigurationCompleted
            ? AUTHENTICATED_ENTRY
            : "/account?firstSetup=1&welcomeMessage=1"
        }
        replace
      />
    )
  }

  return (
    <StudentRouter>
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    </StudentRouter>
  )
}

export default StudentRoutes
